/**
 * AuditPlanSection
 * 
 * Search result section for Audit Planning Tool (EX9)
 * Contains 4 sub-sections:
 * - Planning Documents
 * - Applications
 * - Change Indicators
 * - Certification Messages
 * 
 * Matches exact styling from respective tab components
 * 
 * NOTE: This is a display-only component. Data is fetched by useGlobalSearch hook.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Collapse, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileImageOutlined,
  FileOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import {
  PlanningDocument,
  Application,
  ChangeIndicator,
  CertificationMessage,
} from '../../../../services/auditPlanService';
import HighlightText from '../components/HighlightText';
import { AuditPlanResult } from '../useGlobalSearch';

const { Panel } = Collapse;

// ========================================
// Styled Components - Match Tab styling exactly
// ========================================

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #DCFCE7;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #BBF7D0;
  border-bottom: none;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #166534;
  margin: 0;
`;

const ContentContainer = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  overflow: hidden;
`;

const StyledCollapse = styled(Collapse)`
  border: none;
  background: white;

  .ant-collapse-item {
    border-bottom: 1px solid #E5E7EB;

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    padding: 12px 16px !important;
    background: #F8FAFC;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const SubSectionTitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #374151;
`;

const SubSectionCount = styled.span`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  color: #6B7280;
  margin-left: 8px;
`;

const TableWrapper = styled.div`
  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    padding: 16px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

// File Info Components - Match PlanningDocumentsTab.tsx
const FileInfoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FileIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 20px;
  flex-shrink: 0;
  
  ${({ $type }) => {
    switch ($type) {
      case 'pdf':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'xls':
      case 'xlsx':
        return `
          background: #DCFCE7;
          color: #16A34A;
        `;
      case 'doc':
      case 'docx':
        return `
          background: #DBEAFE;
          color: #2563EB;
        `;
      case 'png':
      case 'jpg':
      case 'jpeg':
        return `
          background: #FEF3C7;
          color: #F59E0B;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const FileName = styled.span`
  font-weight: 500;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: #9CA3AF;
`;

// Application Chips - Match ApplicationsTab.tsx
const NameChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 20px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #1E40AF;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #DBEAFE;
  }
`;

const ScopeChip = styled.span<{ $inScope: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  white-space: nowrap;
  
  ${({ $inScope }) => $inScope ? `
    background: #ECFDF5;
    color: #065F46;
  ` : `
    background: #F3F4F6;
    color: #6B7280;
  `}
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  cursor: default;
  background: white;
  white-space: nowrap;

  ${({ $risk }) => {
    switch ($risk) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const ProcessTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const ControlTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #FEF9C3;
  color: #854D0E;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

// Change Indicator Chips - Match ChangeIndicatorChecklistTab.tsx
const ProcessOwnerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EFF6FF;
  border-radius: 50%;
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  color: #1E40AF;
  flex-shrink: 0;
`;

const OwnerName = styled.span`
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuarterChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #F0FDF4;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #166534;
  white-space: nowrap;
`;

const SectionChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #F3F4F6;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6B7280;
  white-space: nowrap;
`;

const SignificantChangeChip = styled.span<{ $type: 'changeIdentified' | 'noChanges' | 'na' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  white-space: nowrap;
  
  ${({ $type }) => {
    switch ($type) {
      case 'changeIdentified':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'noChanges':
        return `
          background: #DCFCE7;
          color: #166534;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const StatusChip = styled.span<{ $status: 'pending' | 'read' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  white-space: nowrap;
  
  ${({ $status }) => $status === 'pending' ? `
    background: #FEF3C7;
    color: #92400E;
  ` : `
    background: #F3F4F6;
    color: #6B7280;
  `}
`;

const MultiLineCell = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
`;

// Tooltip components
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div<{ $variant?: 'process' | 'control' }>`
  padding: 8px 12px;
  background: ${props => props.$variant === 'control' ? '#FEF9C3' : '#EFF6FF'};
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.$variant === 'control' ? '#854D0E' : '#1E40AF'};
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.$variant === 'control' ? '#FDE68A' : '#DBEAFE'};
    transform: translateX(2px);
  }
`;

// ========================================
// Helper Functions
// ========================================

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getFileIcon = (fileType: string) => {
  switch (fileType) {
    case 'pdf':
      return <FilePdfOutlined />;
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined />;
    case 'png':
    case 'jpg':
    case 'jpeg':
      return <FileImageOutlined />;
    default:
      return <FileOutlined />;
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getSignificantChangeLabel = (value: ChangeIndicator['significantChange']): string => {
  switch (value) {
    case 'changeIdentified':
      return 'Change Identified';
    case 'noChanges':
      return 'No Changes';
    default:
      return 'N/A';
  }
};

// ========================================
// Types
// ========================================

export interface AuditPlanSectionProps {
  data: AuditPlanResult;
  searchText: string;
}

// ========================================
// Component
// ========================================

const AuditPlanSection: React.FC<AuditPlanSectionProps> = ({
  data,
  searchText,
}) => {
  const navigate = useNavigate();

  // Navigation handlers
  const handleNavigateToDocuments = () => {
    navigate('/audit-plan?tab=documents');
  };

  const handleNavigateToApplications = () => {
    navigate('/audit-plan?tab=applications');
  };

  const handleNavigateToChecklist = () => {
    navigate('/audit-plan?tab=checklist');
  };

  const handleNavigateToProcess = (processId: string) => {
    navigate(`/process-hierarchy/${processId}`);
  };

  const handleNavigateToControl = (controlId: string) => {
    navigate(`/control-activities/${controlId}`);
  };

  // Calculate totals
  const totalDocuments = data.documents.length;
  const totalApplications = data.applications.length;
  const totalChangeIndicators = data.changeIndicators.length;
  const totalCertificationMessages = data.certificationMessages.length;
  const totalResults = totalDocuments + totalApplications + totalChangeIndicators + totalCertificationMessages;

  // Planning Documents columns
  const documentColumns: ColumnsType<PlanningDocument> = [
    {
      title: 'File',
      key: 'file',
      width: 350,
      render: (_, record) => (
        <FileInfoCell>
          <FileIcon $type={record.fileType}>
            {getFileIcon(record.fileType)}
          </FileIcon>
          <FileDetails>
            <FileName>
              <HighlightText text={record.fileName} searchText={searchText} />
            </FileName>
            <FileSize>{formatFileSize(record.fileSize)}</FileSize>
          </FileDetails>
        </FileInfoCell>
      ),
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      width: 200,
      render: (text: string) => (
        <HighlightText text={text || '-'} searchText={searchText} />
      ),
    },
    {
      title: 'Upload Date',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      width: 150,
      render: (date: string) => formatDate(date),
    },
  ];

  // Applications columns
  const applicationColumns: ColumnsType<Application> = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (index: number) => index,
    },
    {
      title: 'Application Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name: string) => (
        <NameChip onClick={handleNavigateToApplications}>
          <HighlightText text={name} searchText={searchText} />
        </NameChip>
      ),
    },
    {
      title: 'Audit Scope',
      dataIndex: 'auditScope',
      key: 'auditScope',
      width: 120,
      render: (scope: Application['auditScope']) => (
        <ScopeChip $inScope={scope === 'In Scope'}>
          {scope}
        </ScopeChip>
      ),
    },
    {
      title: 'Risk Ranking',
      dataIndex: 'riskRanking',
      key: 'riskRanking',
      width: 140,
      render: (risk: string) => (
        <RiskChip $risk={risk}>{risk}</RiskChip>
      ),
    },
    {
      title: 'System Administrator',
      dataIndex: 'systemAdministrator',
      key: 'systemAdministrator',
      width: 180,
      render: (text: string) => (
        <HighlightText text={text || '-'} searchText={searchText} />
      ),
    },
    {
      title: 'Processes',
      dataIndex: 'processes',
      key: 'processes',
      width: 200,
      render: (processes: Application['processes']) => {
        if (!processes || processes.length === 0) return '-';
        
        const content = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {processes.slice(0, 2).map((p, idx) => (
              <ProcessTag key={idx} onClick={() => handleNavigateToProcess(p.id)}>
                <HighlightText text={p.name || '-'} searchText={searchText} />
              </ProcessTag>
            ))}
            {processes.length > 2 && (
              <ProcessTag>+{processes.length - 2}</ProcessTag>
            )}
          </div>
        );

        if (processes.length > 1) {
          return (
            <Tooltip
              title={
                <div>
                  <TooltipTitle>Processes</TooltipTitle>
                  <TooltipList>
                    {processes.map((p, idx) => (
                      <TooltipItem key={idx} onClick={() => handleNavigateToProcess(p.id)}>
                        {p.name}
                      </TooltipItem>
                    ))}
                  </TooltipList>
                </div>
              }
              placement="top"
              overlayStyle={{ maxWidth: '400px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color="#FFFFFF"
            >
              {content}
            </Tooltip>
          );
        }

        return content;
      },
    },
    {
      title: 'Controls',
      dataIndex: 'controlActivities',
      key: 'controlActivities',
      width: 200,
      render: (controls: Application['controlActivities']) => {
        if (!controls || controls.length === 0) return '-';
        
        const content = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {controls.slice(0, 2).map((c, idx) => (
              <ControlTag key={idx} onClick={() => handleNavigateToControl(c.id)}>
                <HighlightText text={c.name || '-'} searchText={searchText} />
              </ControlTag>
            ))}
            {controls.length > 2 && (
              <ControlTag>+{controls.length - 2}</ControlTag>
            )}
          </div>
        );

        if (controls.length > 1) {
          return (
            <Tooltip
              title={
                <div>
                  <TooltipTitle>Control Activities</TooltipTitle>
                  <TooltipList>
                    {controls.map((c, idx) => (
                      <TooltipItem key={idx} $variant="control" onClick={() => handleNavigateToControl(c.id)}>
                        {c.name}
                      </TooltipItem>
                    ))}
                  </TooltipList>
                </div>
              }
              placement="top"
              overlayStyle={{ maxWidth: '400px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color="#FFFFFF"
            >
              {content}
            </Tooltip>
          );
        }

        return content;
      },
    },
  ];

  // Change Indicators columns
  const changeIndicatorColumns: ColumnsType<ChangeIndicator> = [
    {
      title: 'Quarter',
      dataIndex: 'quarter',
      key: 'quarter',
      width: 80,
      render: (quarter: string) => (
        <QuarterChip>{quarter}</QuarterChip>
      ),
    },
    {
      title: 'Process Owner',
      dataIndex: 'processOwnerName',
      key: 'processOwnerName',
      width: 180,
      render: (name: string) => (
        <ProcessOwnerCell>
          <Avatar>{name?.charAt(0)?.toUpperCase() || '?'}</Avatar>
          <OwnerName>
            <HighlightText text={name || '-'} searchText={searchText} />
          </OwnerName>
        </ProcessOwnerCell>
      ),
    },
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      render: (text: string) => (
        <Tooltip title={text}>
          <MultiLineCell>
            <HighlightText text={text || '-'} searchText={searchText} />
          </MultiLineCell>
        </Tooltip>
      ),
    },
    {
      title: 'Response',
      dataIndex: 'response',
      key: 'response',
      width: 200,
      render: (text: string) => (
        <Tooltip title={text}>
          <MultiLineCell>
            <HighlightText text={text || '-'} searchText={searchText} />
          </MultiLineCell>
        </Tooltip>
      ),
    },
    {
      title: 'Significant Change',
      dataIndex: 'significantChange',
      key: 'significantChange',
      width: 140,
      render: (value: ChangeIndicator['significantChange']) => (
        <SignificantChangeChip $type={value}>
          {getSignificantChangeLabel(value)}
        </SignificantChangeChip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: ChangeIndicator['status']) => (
        <StatusChip $status={status}>
          {status === 'pending' ? 'Pending' : 'Read'}
        </StatusChip>
      ),
    },
  ];

  // Certification Messages columns
  const certificationMessageColumns: ColumnsType<CertificationMessage> = [
    {
      title: 'Quarter',
      dataIndex: 'quarter',
      key: 'quarter',
      width: 80,
      render: (quarter: string) => (
        <QuarterChip>{quarter}</QuarterChip>
      ),
    },
    {
      title: 'Process Owner',
      dataIndex: 'processOwnerName',
      key: 'processOwnerName',
      width: 180,
      render: (name: string) => (
        <ProcessOwnerCell>
          <Avatar>{name?.charAt(0)?.toUpperCase() || '?'}</Avatar>
          <OwnerName>
            <HighlightText text={name || '-'} searchText={searchText} />
          </OwnerName>
        </ProcessOwnerCell>
      ),
    },
    {
      title: 'Section',
      dataIndex: 'section',
      key: 'section',
      width: 120,
      render: (section: string) => (
        <SectionChip>
          <HighlightText text={section || '-'} searchText={searchText} />
        </SectionChip>
      ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (text: string) => (
        <Tooltip title={text}>
          <MultiLineCell>
            <HighlightText text={text || '-'} searchText={searchText} />
          </MultiLineCell>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: CertificationMessage['status']) => (
        <StatusChip $status={status}>
          {status === 'pending' ? 'Pending' : 'Read'}
        </StatusChip>
      ),
    },
  ];

  // Don't render if no results
  if (totalResults === 0) {
    return null;
  }

  // Determine active panels
  const activePanels: string[] = [];
  if (totalDocuments > 0) activePanels.push('documents');
  if (totalApplications > 0) activePanels.push('applications');
  if (totalChangeIndicators > 0) activePanels.push('changeIndicators');
  if (totalCertificationMessages > 0) activePanels.push('certificationMessages');

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>Audit Planning Tool ({totalResults} results)</SectionTitle>
      </SectionHeader>
      
      <ContentContainer>
        <StyledCollapse defaultActiveKey={activePanels}>
          {/* Planning Documents */}
          {totalDocuments > 0 && (
            <Panel
              key="documents"
              header={
                <>
                  <SubSectionTitle>Planning Documents</SubSectionTitle>
                  <SubSectionCount>({totalDocuments})</SubSectionCount>
                </>
              }
            >
              <TableWrapper>
                <Table
                  columns={documentColumns}
                  dataSource={data.documents}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  onRow={() => ({
                    onClick: handleNavigateToDocuments,
                    style: { cursor: 'pointer' },
                  })}
                />
              </TableWrapper>
            </Panel>
          )}

          {/* Applications */}
          {totalApplications > 0 && (
            <Panel
              key="applications"
              header={
                <>
                  <SubSectionTitle>Applications</SubSectionTitle>
                  <SubSectionCount>({totalApplications})</SubSectionCount>
                </>
              }
            >
              <TableWrapper>
                <Table
                  columns={applicationColumns}
                  dataSource={data.applications}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 1200 }}
                />
              </TableWrapper>
            </Panel>
          )}

          {/* Change Indicators */}
          {totalChangeIndicators > 0 && (
            <Panel
              key="changeIndicators"
              header={
                <>
                  <SubSectionTitle>Change Indicators</SubSectionTitle>
                  <SubSectionCount>({totalChangeIndicators})</SubSectionCount>
                </>
              }
            >
              <TableWrapper>
                <Table
                  columns={changeIndicatorColumns}
                  dataSource={data.changeIndicators}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  onRow={() => ({
                    onClick: handleNavigateToChecklist,
                    style: { cursor: 'pointer' },
                  })}
                />
              </TableWrapper>
            </Panel>
          )}

          {/* Certification Messages */}
          {totalCertificationMessages > 0 && (
            <Panel
              key="certificationMessages"
              header={
                <>
                  <SubSectionTitle>Certification Messages</SubSectionTitle>
                  <SubSectionCount>({totalCertificationMessages})</SubSectionCount>
                </>
              }
            >
              <TableWrapper>
                <Table
                  columns={certificationMessageColumns}
                  dataSource={data.certificationMessages}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  onRow={() => ({
                    onClick: handleNavigateToChecklist,
                    style: { cursor: 'pointer' },
                  })}
                />
              </TableWrapper>
            </Panel>
          )}
        </StyledCollapse>
      </ContentContainer>
    </SectionContainer>
  );
};

export default AuditPlanSection;
